import React, { useEffect, useState } from 'react'
import {
  Box,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Button } from '../../components/Button'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { profileSchema } from './validators'
import { useTranslation } from 'react-i18next'
import { FormInputText } from '../../components/FormInputText'
import { FormInputDatePicker } from '../../components/FormInputDatePicker'
import { FormInputSelect } from '../../components/FormInputSelect'
import { GenreDB } from '../../domain/db/GenreOptions'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  getProfessionalProfile,
  showModalCropImage,
  updateProfessionalProfile
} from '../../redux/reducers/profile/profile.actions'
import { selectedProfessional } from '../../redux/reducers/profile/profile.selectors'
import { initialBreadcrumb } from '../../redux/reducers/breadcrumb/actions'
import { AvatarProfile } from '../../components/AvatarProfile'
import dayjs from 'dayjs'
import { showMessage } from '../../redux/reducers/snackbar/snackbar.actions'
import { ModalCropImage } from './components/ModalCropImage'

export interface ProfileInputValues {
  name: string
  birthday: string
  gender: string
  email: string
  whatsapp: string
}

export const Profile = () => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const profile = useAppSelector(selectedProfessional)

  const showModal = () => dispatch(showModalCropImage({ url: profile?.imagePath }))

  useEffect(() => {
    dispatch(initialBreadcrumb({ label: t('profile.title'), path: location.pathname }))
    load().catch(console.log)
  }, [])

  const load = async () => {
    await dispatch(getProfessionalProfile()).unwrap()
  }

  useEffect(() => {
    if (profile) {
      const { name, email, gender, birthday } = profile
      reset({ name, email, gender, birthday })
    }
  }, [profile])

  const { control, handleSubmit, reset, formState: { errors } } = useForm<ProfileInputValues>({
    resolver: yupResolver(profileSchema)
  })

  const handleSave = async (data: ProfileInputValues) => {
    try {
      setIsLoading(true)
      data.birthday = dayjs(data.birthday).format('YYYY-MM-DD')
      await dispatch(updateProfessionalProfile({ payload: data }))
      dispatch(showMessage({ message: t('general.message.successDescription') }))
    } catch (e) {
      dispatch(showMessage({ message: t('general.message.errorDescription'), type: 'error' }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack>
      <ModalCropImage />
      <Stack>
        <Box sx={{ width: '100%', typography: 'body1' }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3} >
              <Box borderRadius={1} border={0.5} borderColor={palette.grey[600]} bgcolor={palette.grey[800]}>
                <Stack justifyContent="center" alignItems="center" spacing={2} p={4}>
                  <AvatarProfile url={profile?.imagePath} name={profile?.name} onClick={showModal} />
                  <Stack alignItems="center">
                    <Typography fontWeight="300" color={palette.grey[400]} fontSize={16}>{profile?.email}</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Box border={0.5} borderColor={palette.grey[600]} borderRadius={1} bgcolor={palette.grey[800]}>
                <Stack justifyContent="center" pl={2} py={2}>{t('profile.updateTitle')}</Stack>
                <Divider />
                <Stack justifyContent="center" p={2} spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormInputText
                        name="email"
                        required
                        disabled={true}
                        control={control}
                        label={`${t('general.field.email')}`}
                        errorMessage={errors.email?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} lg={6}>
                      <FormInputText
                        autoFocus
                        name="name"
                        required
                        control={control}
                        label={`${t('general.field.name')}`}
                        errorMessage={errors.name?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} lg={6}>
                      <FormInputSelect
                        name="gender"
                        required
                        value={profile?.gender}
                        control={control}
                        label={`${t('general.field.gender')}`}
                        items={GenreDB}
                        errorMessage={errors.gender?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} lg={6}>
                      <FormInputDatePicker
                        control={control}
                        name="birthday"
                        required={true}
                        value={profile?.birthday}
                        label={`${t('general.field.birthday')}`}
                        errorMessage={errors.birthday?.message}
                      />
                    </Grid>
                    <Grid item xs={12} textAlign="right">
                      <Button
                        title={`${t('general.btn.update')}`}
                        isLoading={isLoading}
                        onClick={handleSubmit(handleSave)}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Stack>
  )
}
